import { Navigate, useParams } from 'react-router-dom';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HeroQuickStart from './landing/HeroQuickStart';
import { INDICATIVE_RATE, INDICATIVE_RATE_PCT } from '../lib/finance';
import { verticalBySlug } from '../config/verticals';

// The ONE indicative rate, same as the home page. Hardcoding a different number here
// quoted its own rate in the calculator while the copy beside it quoted another — a finance page
// showing two rates is exactly what `finance.js` exists to prevent.
const HERO_RATE = INDICATIVE_RATE;

/**
 * A finance vertical landing page — /finance/jdm, /finance/utes, and so on.
 *
 * ONE component for all six. They differ in headline, copy, calculator starting point and share
 * image, and in nothing else: the funnel is identical everywhere (owner's decision, 2026-08-18),
 * so six page components would be six copies of the same wiring waiting to drift apart. Everything
 * that varies lives in `config/verticals.js`.
 *
 * The hero is DARK, matching the home page, and that is a functional requirement rather than a
 * stylistic one: `HeroQuickStart` is built for a dark backdrop — `bg-white/10` with white text —
 * so dropping it on a white page renders the primary call to action as white-on-white. An earlier
 * version of this page did exactly that.
 *
 * What this page does NOT do is set its own meta tags. This is a client-rendered SPA, and the
 * scrapers that matter for a share card — Facebook, LinkedIn, X — do not run JavaScript. Anything
 * set here would be invisible to exactly the audience it was for. The per-page title, description,
 * canonical and OG image are baked into static HTML at build time by `scripts/prerender-routes.js`,
 * and this component's rendered output is written into that file by `scripts/prerender-body.js` —
 * so the page arrives with its words in it rather than an empty root div.
 */
export default function VerticalLanding() {
  const { slug } = useParams();
  const vertical = verticalBySlug(slug);

  // An unknown slug goes home rather than rendering an empty shell. `replace` so the dead URL does
  // not sit in history waiting for a Back button to land on it again.
  if (!vertical) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative overflow-hidden pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="absolute inset-0 bg-black" />

        <div className="relative mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span className="mb-4 inline-block rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-white">
                {vertical.name}
              </span>

              <h1 className="mb-6 font-outfit text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
                {vertical.h1}
              </h1>

              <p className="mb-8 max-w-lg text-lg leading-relaxed text-gray-300 md:text-xl">
                {vertical.subhead}
              </p>

              <ul className="space-y-3 text-gray-300">
                <li className="flex gap-3">
                  <span aria-hidden="true" className="text-[#00D47E]">✓</span>
                  Quick approvals, great rates
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="text-[#00D47E]">✓</span>
                  A specialist who knows these cars — not a call centre
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="text-[#00D47E]">✓</span>
                  Free to check, and no obligation to go ahead
                </li>
              </ul>

              <p className="mt-8 max-w-lg text-sm text-gray-400">
                Indicative rate from {INDICATIVE_RATE_PCT}% p.a. Your rate depends on your
                circumstances and the vehicle. Lending criteria, fees and T&amp;Cs apply.
              </p>
            </div>

            {/* The same calculator the home page runs, started where this vertical's buyers
                actually shop. Continue lands in the same three-step funnel — one funnel, six
                front doors. */}
            <div>
              <HeroQuickStart
                rate={HERO_RATE}
                defaults={vertical.defaults}
                vehicleCategory={vertical.vehicleCategory}
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
